import { Typography } from '@mui/material'
import React, { ChangeEvent, memo, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { Formik, FormikProps } from 'formik'
import { BankInformationSchema, DebitCardSchema } from './BankPaymentForm.validator'
import {
  BankInformationRequest,
  BankPaymentFormProps,
  BankPaymentFormValues,
  DebitCardRequest,
} from './BankPaymentForm.interface'
import { BankPaymentFormType } from './BankPaymentForm.constants'

import { Button } from 'src/components/shared/Button'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import { FormikRadioGroup } from 'src/components/shared/form/formik/FormikRadioGroup'
import { DebitCardContent } from './DebitCardContent'
import { BankInformationContent } from './BankInformationContent'
import { GSIcon } from 'src/components/shared/GSIcon'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { getPaymentMethodDetails, PaymentData } from 'src/utils/paymentMethod'
import { FetchPaymentMethod } from 'src/constants/actionTypes'
import { DriverPaymentMethod, PaymentMethodTypeEnum } from 'src/interfaces/paymentMethod'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { CollectDataStepIndex } from 'src/components/client/CollectDataStepIndex'
import { getDriver } from 'src/selectors/driver'
import { InfoContainer } from '../shared/InfoContainer'

const paymentMethodOptions = [{ label: 'Debit Card', value: BankPaymentFormType.DEBIT_CARD }]

const initialValues: BankPaymentFormValues = {
  nameOnCard: '',
  cardNumber: '',
  expirationDate: '',
  zipCode: '',
  routingNumber: '',
  accountNumber: '',
  accountType: undefined,
  formType: undefined,
}

const getValidationSchema = (type?: BankPaymentFormType) => {
  const schemaMap = {
    [BankPaymentFormType.DEBIT_CARD]: DebitCardSchema,
    [BankPaymentFormType.BANK_INFORMATION]: BankInformationSchema,
  }

  if (!type) {
    return undefined
  }

  return schemaMap[type]
}

const BankPaymentFormComponent = ({
  title,
  description,
  showIcon,
  showStepIndex,
  showCurrentPaymentMethod,
  showFormLabel = true,
  onSubmitDebitCard,
  onSubmitBankInformation,
  paymentMethodData: paymentMethodDataProp,
  flow,
}: BankPaymentFormProps) => {
  const dispatch = useDispatch()
  const currentStep = useSelector(getCurrentCollectDataStep)
  const driver = useSelector(getDriver)
  const formikRef = useRef<FormikProps<BankPaymentFormValues>>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [currentFormType, setCurrentFormType] = useState<BankPaymentFormType | undefined>(undefined)
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentData | null>(null)
  const [paymentMethodData, setPaymentMethodData] = useState<DriverPaymentMethod | null>(
    paymentMethodDataProp || null,
  )

  const hasBusinessEntity = paymentMethodData?.hasBusinessEntity || false

  const getPaymentMethodLabel = (method: PaymentData): string => {
    return method?.type === PaymentMethodTypeEnum.Card ? 'Debit Card' : 'Bank Information'
  }

  useEffect(() => {
    const processPaymentMethod = (method: DriverPaymentMethod) => {
      if (showCurrentPaymentMethod) {
        const isCard = method.paymentMethodType === PaymentMethodTypeEnum.Card
        const mapped = isCard
          ? {
              type: method.paymentMethodType,
              lastDigits: method.cardNumberLast4 || '',
              expiration: method.cardExpiration || '',
            }
          : {
              type: method.paymentMethodType,
              accountLastDigits: method.bankAccountNumberLast4 || '',
              routingLastDigits: method.bankRoutingNumberLast4 || '',
            }
        setCurrentPaymentMethod(mapped)
      }
    }

    if (paymentMethodDataProp) {
      setPaymentMethodData(paymentMethodDataProp)
      processPaymentMethod(paymentMethodDataProp)

      return
    }

    const fetchPaymentMethod = async () => {
      try {
        const method: DriverPaymentMethod = await createAsyncAction(
          dispatch,
          FetchPaymentMethod.request({ flow }),
        )

        setPaymentMethodData(method)
        processPaymentMethod(method)
      } catch (e) {
        setPaymentMethodData(null)
        setCurrentPaymentMethod(null)
      }
    }

    fetchPaymentMethod()
  }, [dispatch, flow, showCurrentPaymentMethod, paymentMethodDataProp])

  const handleFormSubmit = async (values: BankPaymentFormValues) => {
    setIsLoading(true)
    setError(null)

    let formattedDate = ''

    if (values.expirationDate) {
      const cleanDate = values.expirationDate
        .replace(/undefined/g, '')
        .replace(/--/g, '-')
        .trim()
      formattedDate = cleanDate
    }

    try {
      const currentType = (values.formType as BankPaymentFormType) || BankPaymentFormType.DEBIT_CARD
      let result = null

      if (currentType === BankPaymentFormType.DEBIT_CARD) {
        const cleanCardNumber = (values.cardNumber || '').replace(/\s+/g, '')
        const requestBody: DebitCardRequest = {
          cardNumber: cleanCardNumber,
          cardExpiration: formattedDate,
          zipcode: values.zipCode || '',
          ...(currentStep?.id && { stepId: currentStep.id }),
        }

        result = await onSubmitDebitCard(requestBody)
      } else if (currentType === BankPaymentFormType.BANK_INFORMATION) {
        const requestBody: BankInformationRequest = {
          routingNumber: values.routingNumber || '',
          accountNumber: values.accountNumber || '',
          accountType: values.accountType || '',
          ...(currentStep?.id && { stepId: currentStep.id }),
          hasBusinessEntity: hasBusinessEntity,
        }

        result = await onSubmitBankInformation(requestBody)
      }

      if (!result) {
        const needsFormTypeRestore = values.formType && !currentFormType

        if (needsFormTypeRestore) {
          setCurrentFormType(values.formType)
        }

        return
      }

      formikRef.current?.resetForm()
      setCurrentFormType(undefined)
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const getPrefilledName = (isBankInformation: boolean): string => {
    if (isBankInformation && hasBusinessEntity) {
      return paymentMethodData?.businessEntityName?.trim() ?? ''
    }

    if (driver) {
      return `${driver.firstName} ${driver.lastName}`.trim()
    }

    return ''
  }

  const handleTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value as BankPaymentFormType

    setCurrentFormType(newType)

    formikRef.current?.setFieldValue('formType', newType)

    const prefilledName = getPrefilledName(newType === BankPaymentFormType.BANK_INFORMATION)

    if (prefilledName) {
      formikRef.current?.setFieldValue('nameOnCard', prefilledName)
    }
  }

  return (
    <Formik
      initialValues={{
        ...initialValues,
      }}
      onSubmit={handleFormSubmit}
      validationSchema={getValidationSchema(currentFormType)}
      innerRef={formikRef}
    >
      {({ handleSubmit, handleChange }) => {
        return (
          <div css={tw`h-full w-full flex flex-col justify-between`}>
            <div>
              {showIcon && (
                <div
                  css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-white mt-2 mb-4`}
                >
                  <GSIcon iconName="gs_wallet_3" height={20} width={20} />
                </div>
              )}
              {showStepIndex && <CollectDataStepIndex stepId={currentStep?.id} />}
              <Typography css={tw`my-2`} variant="h2">
                {title}
              </Typography>
              {description && (
                <Typography css={tw`mb-2 text-shades-400`}>
                  {hasBusinessEntity
                    ? "To receive payouts and process transactions, you'll need to add a valid payment method."
                    : description}
                </Typography>
              )}

              {showCurrentPaymentMethod && currentPaymentMethod && (
                <>
                  <div css={tw`mt-4`}>
                    <Typography css={tw`text-shades-400 uppercase`} variant="overline">
                      Added payment method
                    </Typography>
                  </div>
                  <div css={tw`flex mt-2 items-center flex-row p-4 rounded-2xl bg-shades-0`}>
                    <div>
                      <Typography css={tw`text-shades-1000`} variant="h4">
                        {getPaymentMethodLabel(currentPaymentMethod)}
                      </Typography>
                      <Typography css={tw`mt-0.5 text-shades-400 capitalize`}>
                        {getPaymentMethodDetails(currentPaymentMethod)}
                      </Typography>
                    </div>
                  </div>
                </>
              )}

              <div css={tw`mt-6`}>
                <InfoContainer className={tw`mb-2`}>
                  {hasBusinessEntity
                    ? "Since you've provided business information, please add a business payment method. Contact support if you need help."
                    : 'No business information provided — please add your personal payment details to continue. Contact support if you need help.'}
                </InfoContainer>

                {showFormLabel && (
                  <Typography
                    css={tw`text-shades-400 text-xs font-bold uppercase`}
                    variant="overline"
                  >
                    Add payment method
                  </Typography>
                )}

                <div css={tw`bg-shades-0 p-4 rounded-xl mt-1.5 relative overflow-hidden`}>
                  <div css={tw`w-full mb-4`}>
                    <FormikRadioGroup
                      label="Select preferable option"
                      name="formType"
                      options={[
                        ...paymentMethodOptions,
                        hasBusinessEntity
                          ? {
                              label: 'Business Bank Information',
                              value: BankPaymentFormType.BANK_INFORMATION,
                            }
                          : {
                              label: 'Bank Information',
                              value: BankPaymentFormType.BANK_INFORMATION,
                            },
                      ]}
                      onChange={handleTypeChange}
                    />
                  </div>
                  {currentFormType ? (
                    <>
                      {currentFormType === BankPaymentFormType.DEBIT_CARD && (
                        <DebitCardContent handleChange={handleChange} driver={driver} />
                      )}

                      {currentFormType === BankPaymentFormType.BANK_INFORMATION && (
                        <BankInformationContent
                          hasBusinessEntity={hasBusinessEntity}
                          handleChange={handleChange}
                        />
                      )}

                      <Button
                        className={tw`w-full mt-6`}
                        loading={isLoading}
                        onClick={() => handleSubmit()}
                      >
                        Submit and Continue
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <FormErrorMessage css={tw`mb-4`} error={error} />
          </div>
        )
      }}
    </Formik>
  )
}

export const BankPaymentForm = memo(BankPaymentFormComponent)
